// Sharp gate for the proof PDFs (flag REACT_APP_PDF_WAIT_SHARP_ENABLED,
// customer case 18/9-2026 "Kroppedal Museum": item 1095073 was embedded as a
// 60x68 px thumbnail scaled to ~2000 px - blurred, without any warning).
//
// Pure module: no flag reads, no API client, no DOM - so enviroment.ts can
// import the schedule parser without a cycle and every rule is unit-testable
// without timers or images. The PDF generator wires in the real GetLogo
// re-fetch, the real image measuring and the real clock.
import type { BaseLogo } from "../../core/models/entities/BaseLogo";
import { parseHealDelaysMs } from "./healDelays";

// A logo image whose longest side is below this is not a genuine high-res
// (the API's hd_ blob is 1000 px on the longest side; an imported logo's
// stored image is 1 px per mm, e.g. 60x68 px). Same number as the API's
// ProofMinSharpPixels.
export const PDF_MIN_SHARP_PX = 400;

// Owner rule (Nichlas 9/9): at most 3 GetLogo per logo per action = the
// first fetch + at most TWO re-fetches. The env override is capped here, so
// a longer list in the yml can never widen that budget.
export const MAX_PDF_SHARP_REFETCHES = 2;

// +6 s and +24 s = 30 s in total. With the API's queue reason "proof"
// (bypasses the background gate) the hd blob typically lands in ~1 s; in the
// slow supplier period of 18/9 a render took 11-30 s.
export const DEFAULT_PDF_SHARP_WAIT_MS: readonly number[] = [6_000, 24_000];

// displayImage.highResRetryAfterMs (API hint) is clamped to this window and
// only ever replaces the FIRST wait step - it never adds a step.
export const PDF_SHARP_RETRY_HINT_MIN_MS = 3_000;
export const PDF_SHARP_RETRY_HINT_MAX_MS = 30_000;

/**
 * Parses REACT_APP_PDF_SHARP_WAIT_MS ("6000,24000"). Same guards as
 * parseHealDelaysMs (invalid/blank = the default), then capped to
 * MAX_PDF_SHARP_REFETCHES entries.
 */
export function parsePdfSharpWaitMs(raw: string | undefined): number[] {
  return parseHealDelaysMs(raw, DEFAULT_PDF_SHARP_WAIT_MS).slice(
    0,
    MAX_PDF_SHARP_REFETCHES,
  );
}

export interface NaturalSize {
  naturalWidth: number;
  naturalHeight: number;
}

// null = the image could not be measured (load error / no URL).
export type MeasureImage = (url: string) => Promise<NaturalSize | null>;

export interface UnsharpLogoInfo {
  id: number;
  title: string;
  // Item no. (product SKU) when the logo has one, else the supplier serial.
  serial: string;
}

const PDF_NOT_SHARP_ERROR_NAME = "PdfNotSharpError";

/**
 * Thrown by the PDF generator INSTEAD of saving a PDF that would silently
 * embed a low-resolution logo. Nothing has been downloaded when it is thrown.
 * `downloadAnyway` finishes the PDF with exactly the data the stopped run had
 * already fetched - 0 new calls.
 */
export class PdfNotSharpError extends Error {
  logos: UnsharpLogoInfo[];
  downloadAnyway: () => Promise<void>;

  constructor(logos: UnsharpLogoInfo[], downloadAnyway: () => Promise<void>) {
    super("PDF stopped: one or more logo images are not sharp yet");
    this.name = PDF_NOT_SHARP_ERROR_NAME;
    this.logos = logos;
    this.downloadAnyway = downloadAnyway;
    Object.setPrototypeOf(this, PdfNotSharpError.prototype);
  }
}

export function isPdfNotSharpError(error: unknown): error is PdfNotSharpError {
  return (
    error instanceof PdfNotSharpError ||
    (!!error &&
      typeof error === "object" &&
      (error as { name?: unknown }).name === PDF_NOT_SHARP_ERROR_NAME &&
      Array.isArray((error as { logos?: unknown }).logos))
  );
}

const PDF_SHARP_CANCELLED_ERROR_NAME = "PdfSharpCancelledError";

/**
 * Thrown by the PDF generator when the USER cancelled the bounded sharp wait
 * (PdfSharpOptions.signal). Nothing has been downloaded, and nothing must be
 * shown for it: no dialog, no error toast, no success toast.
 */
export class PdfSharpCancelledError extends Error {
  constructor() {
    super("PDF cancelled while waiting for sharp logo images");
    this.name = PDF_SHARP_CANCELLED_ERROR_NAME;
    Object.setPrototypeOf(this, PdfSharpCancelledError.prototype);
  }
}

export function isPdfSharpCancelledError(
  error: unknown,
): error is PdfSharpCancelledError {
  return (
    error instanceof PdfSharpCancelledError ||
    (!!error &&
      typeof error === "object" &&
      (error as { name?: unknown }).name === PDF_SHARP_CANCELLED_ERROR_NAME)
  );
}

// The two outcomes the caller handles itself (a dialog / nothing at all) -
// not generator failures, so they are not logged as errors.
export function isPdfSharpHandledStop(error: unknown): boolean {
  return isPdfNotSharpError(error) || isPdfSharpCancelledError(error);
}

/**
 * Only IMAGE logos are gated. A name/number/text/symbol logo's composite can
 * legitimately be small (a number composite is e.g. 185x120), so gating it
 * would stop valid PDFs. Mapped logos and the public proofing DTOs carry
 * `logoType` - that decides alone.
 *
 * The raw js-upload for-pdf DTOs (ProofPdfDownloadScreen) carry NO logoType:
 * `font` is set for name/number/text logos, but a SYMBOL logo has neither a
 * font nor a highResUrl (the API never renders one for it). "No font" is
 * therefore not proof of an image logo - judging a symbol logo as unsharp
 * cost 30 s of waiting and 2 extra WHOLE batch calls per download, then a
 * false "still being prepared" dialog. So a raw DTO is only gated on
 * POSITIVE evidence: the API's own highResPending === true (only ever set
 * for image logos), or a highResUrl that the pixel rule can judge (not for a
 * DTO with a digit template = a number logo). A raw DTO without logoType,
 * font and highResUrl is skipped, exactly as before the gate (its page has
 * no image - blank, never blurred).
 */
export function isSharpGatedLogo(logo: BaseLogo | null | undefined): boolean {
  if (!logo) return false;
  const logoType = (logo as { logoType?: unknown }).logoType;
  if (typeof logoType === "string" && logoType.length > 0) {
    return logoType === "ImageLogo";
  }
  if ((logo as { font?: unknown }).font) return false;
  const image = logo.displayImage;
  if (!image) return false;
  if (image.highResPending === true) return true;
  // A digit template is only ever sent for a NUMBER logo (also one whose
  // font happens to be null) - its composite is legitimately small.
  if (image.digitUrl) return false;
  return !!image.highResUrl;
}

function longestSide(natural: NaturalSize | null | undefined): number | null {
  if (!natural) return null;
  const longest = Math.max(natural.naturalWidth || 0, natural.naturalHeight || 0);
  return longest > 0 ? longest : null;
}

/**
 * THE sharpness rule (the shared contract's "FE's SKARPHEDSREGEL" - valid
 * with and without the API's highResPending field). An image logo is NOT
 * sharp when at least one of these holds:
 *   - displayImage.highResPending === true (API: "highResUrl is not a
 *     genuine high-res"; mapper: "the server sent no highResUrl"),
 *   - highResUrl is missing,
 *   - highResUrl === lowResUrl (the mapper's cold fallback - today's
 *     isColdHighResMiss),
 *   - the image's natural longest side is below PDF_MIN_SHARP_PX.
 * Unknown dimensions (natural = null) leave the verdict to the flag/equality
 * rules. Non-image logos are always "sharp" (not gated).
 */
export function isImageLogoSharp(
  logo: BaseLogo,
  natural: NaturalSize | null | undefined,
): boolean {
  if (!isSharpGatedLogo(logo)) return true;
  return isGatedImageSharp(logo, natural);
}

// The rules alone, for a logo that is ALREADY known to be gated. The wait
// uses this for the re-fetched answers: a logo that was gated when the PDF
// started stays gated for the whole run, whatever a later (raw) answer
// happens to carry - an answer that lost its highResUrl is "not sharp",
// never "not an image logo any more".
function isGatedImageSharp(
  logo: BaseLogo,
  natural: NaturalSize | null | undefined,
): boolean {
  const image = logo.displayImage;
  if (!image) return false;
  if (image.highResPending === true) return false;
  if (!image.highResUrl) return false;
  if (image.highResUrl === image.lowResUrl) return false;
  const longest = longestSide(natural);
  if (longest !== null && longest < PDF_MIN_SHARP_PX) return false;
  return true;
}

async function measureLogo(
  logo: BaseLogo,
  measure: MeasureImage | undefined,
): Promise<NaturalSize | null> {
  const url = logo?.displayImage?.highResUrl;
  if (!measure || !url) return null;
  try {
    return await measure(url);
  } catch {
    return null;
  }
}

/**
 * Indexes (into `logos`) of the gated logos that are not sharp right now.
 * `alreadyGated` = every entry is a re-fetched answer for a logo that was
 * gated when the PDF started (see isGatedImageSharp).
 */
export async function findUnsharpLogoIndexes(
  logos: BaseLogo[],
  measure?: MeasureImage,
  alreadyGated = false,
): Promise<number[]> {
  const verdicts = await Promise.all(
    logos.map(async (logo) => {
      if (!alreadyGated && !isSharpGatedLogo(logo)) return true;
      if (!logo) return false;
      // The cheap rules first: no image load for a logo they already fail.
      if (!isGatedImageSharp(logo, null)) return false;
      return isGatedImageSharp(logo, await measureLogo(logo, measure));
    }),
  );
  return verdicts.flatMap((sharp, index) => (sharp ? [] : [index]));
}

export function toUnsharpLogoInfo(logo: BaseLogo): UnsharpLogoInfo {
  return {
    id: logo.id,
    title: logo.title ?? "",
    serial: logo.productSku || logo.product?.sku || logo.serial || "",
  };
}

// First wait step: the API's retry hint (largest among the waiting logos),
// clamped. No hint = the schedule's own first step.
function firstDelayMs(
  logos: BaseLogo[],
  unsharp: number[],
  scheduled: number,
): number {
  let hint: number | undefined;
  for (const index of unsharp) {
    const value = logos[index]?.displayImage?.highResRetryAfterMs;
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      hint = hint === undefined ? value : Math.max(hint, value);
    }
  }
  if (hint === undefined) return scheduled;
  return Math.min(
    PDF_SHARP_RETRY_HINT_MAX_MS,
    Math.max(PDF_SHARP_RETRY_HINT_MIN_MS, hint),
  );
}

/**
 * Re-reads the logos that are not sharp yet. Answers one entry per requested
 * logo, in order; null/undefined = "no answer, keep what you have". Must
 * never widen the budget: ONE call per requested logo (refetchEach) or ONE
 * batch call in total (refetchFromBatch) per step.
 */
export type RefetchUnsharp = (
  unsharp: BaseLogo[],
) => Promise<(BaseLogo | null | undefined)[]>;

/** One GetLogo per unsharp logo; a failed call keeps the previous answer. */
export function refetchEach(
  refetchOne: (logo: BaseLogo) => Promise<BaseLogo>,
): RefetchUnsharp {
  return async (unsharp) => {
    const results = await Promise.allSettled(
      unsharp.map((logo) => refetchOne(logo)),
    );
    return results.map((result) =>
      result.status === "fulfilled" ? result.value : null,
    );
  };
}

/**
 * The public proof screens have no GetLogo - they repeat their ONE batch
 * call and the fresh rows are matched back by logo id.
 */
export function refetchFromBatch(
  refetchAll: () => Promise<BaseLogo[]>,
): RefetchUnsharp {
  return async (unsharp) => {
    const fresh = await refetchAll();
    const byId = new Map<number, BaseLogo>();
    for (const logo of fresh ?? []) {
      if (logo && typeof logo.id === "number" && !byId.has(logo.id)) {
        byId.set(logo.id, logo);
      }
    }
    return unsharp.map((logo) => byId.get(logo.id) ?? null);
  };
}

export interface WaitForSharpDeps {
  measure?: MeasureImage;
  // Injected in tests; the generator uses the real clock.
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  // The user's "Cancel" on the loading toast: a running wait step ends at
  // once (its timer is cleared) and NO further re-fetch is made.
  signal?: AbortSignal;
}

// Resolves after `ms` - or at once when `signal` aborts (the timer is
// cleared, nothing is left running). Never rejects.
export const abortableSleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * Bounded wait for the background hd render the first fetch kicked off.
 * Per step: wait, re-fetch ONLY the logos that are still not sharp, swap in
 * the answers that are sharp now (in place, like retryColdHighResLogos).
 * At most MAX_PDF_SHARP_REFETCHES steps whatever `delaysMs` holds, no step at
 * all when everything is sharp. Never throws: a failed re-fetch (or a failed
 * measuring) keeps the previous answer.
 *
 * Returns the indexes (into `logos`) that are STILL not sharp.
 * `onStatus(ready, total)` counts the gated (image) logos only.
 */
export async function waitForSharpLogos(
  logos: BaseLogo[],
  refetch: RefetchUnsharp,
  delaysMs: readonly number[],
  onStatus?: (ready: number, total: number) => void,
  deps: WaitForSharpDeps = {},
): Promise<number[]> {
  const sleep = deps.sleep ?? abortableSleep;
  const signal = deps.signal;
  const total = logos.filter((logo) => isSharpGatedLogo(logo)).length;
  const report = (unsharpCount: number) => {
    if (!onStatus) return;
    try {
      onStatus(Math.max(0, total - unsharpCount), total);
    } catch {
      // A status listener can never fail the PDF.
    }
  };

  let unsharp = await findUnsharpLogoIndexes(logos, deps.measure);
  const steps = delaysMs.slice(0, MAX_PDF_SHARP_REFETCHES);
  // No status at all for a PDF whose logos are sharp from the start (the
  // normal, warm case): the download looks exactly like before.
  let waited = false;

  for (let step = 0; step < steps.length && unsharp.length > 0; step++) {
    if (signal?.aborted) break;
    waited = true;
    report(unsharp.length);
    const delay =
      step === 0 ? firstDelayMs(logos, unsharp, steps[0]) : steps[step];
    await (signal ? sleep(delay, signal) : sleep(delay));
    // Cancelled while waiting: no further re-fetch (0 more calls).
    if (signal?.aborted) break;

    let answers: (BaseLogo | null | undefined)[] = [];
    try {
      answers = await refetch(unsharp.map((index) => logos[index]));
    } catch (error) {
      console.warn(
        "Sharp wait: re-fetch failed; keeping the previous logo images:",
        error,
      );
      continue;
    }

    const candidates = unsharp.flatMap((index, i) => {
      const answer = answers[i];
      return answer ? [{ index, answer }] : [];
    });
    const stillUnsharp = new Set(
      (
        await findUnsharpLogoIndexes(
          candidates.map((c) => c.answer),
          deps.measure,
          // Gated when the PDF started = gated for the whole run.
          true,
        )
      ).map((i) => candidates[i].index),
    );
    for (const { index, answer } of candidates) {
      if (!stillUnsharp.has(index)) logos[index] = answer;
    }
    const swapped = new Set(
      candidates
        .filter((c) => !stillUnsharp.has(c.index))
        .map((c) => c.index),
    );
    unsharp = unsharp.filter((index) => !swapped.has(index));
  }

  if (waited) report(unsharp.length);
  return unsharp;
}
